(() => {
  const bundles = document.querySelectorAll("[data-onura-product-bundle]");

  if (!bundles.length) return;

  const getCartSections = () => {
    const cartDrawer = document.querySelector("cart-drawer");

    if (cartDrawer && typeof cartDrawer.getSectionsToRender === "function") {
      return cartDrawer
        .getSectionsToRender()
        .map((section) => section.section || section.id)
        .filter(Boolean);
    }

    return ["cart-drawer", "cart-icon-bubble"];
  };

  const addItemsToCart = async (items) => {
    const root = window.Shopify?.routes?.root || "/";
    const response = await fetch(`${root}cart/add.js`, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify({
        items,
        sections: getCartSections(),
        sections_url: `${root}cart`
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => null);
      throw new Error(error?.description || error?.message || "Unable to add this bundle to the cart.");
    }

    return response.json();
  };

  const createBundleId = () => {
    if (window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }

    if (window.crypto?.getRandomValues) {
      const values = new Uint32Array(4);
      window.crypto.getRandomValues(values);
      return `onura-${Date.now().toString(36)}-${Array.from(values, (value) => value.toString(36)).join("")}`;
    }

    return `onura-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  };

  const getBundleQuantity = (bundle) => {
    return Math.max(1, Number(bundle.getAttribute("data-bundle-quantity")) || 1);
  };

  const initProductGallery = (bundle) => {
    const defaultMediaId = bundle.getAttribute("data-default-media-id");
    const mediaSlides = bundle.querySelectorAll("[data-onura-media-slide]");
    const mediaThumbs = bundle.querySelectorAll("[data-onura-media-thumb]");
    const mediaThumbsWrapper = bundle.querySelector("[data-onura-product-thumbs]");

    if (!mediaSlides.length) return;

    const revealAllMedia = () => {
      mediaSlides.forEach((slide) => {
        slide.classList.remove("is-hidden-by-variant");
        slide.removeAttribute("hidden");
      });

      mediaThumbs.forEach((thumb) => {
        thumb.classList.remove("is-hidden-by-variant");
        thumb.removeAttribute("hidden");
      });
    };

    const setActiveMedia = (mediaId) => {
      if (!mediaId) return;

      revealAllMedia();

      mediaSlides.forEach((slide) => {
        slide.classList.toggle("is-active", slide.getAttribute("data-media-id") === String(mediaId));
      });

      mediaThumbs.forEach((thumb) => {
        thumb.classList.toggle("is-active", thumb.getAttribute("data-media-id") === String(mediaId));
      });
    };

    const getInitialMediaId = () => {
      if (defaultMediaId) return defaultMediaId;

      const activeSlide = bundle.querySelector("[data-onura-media-slide].is-active");
      if (activeSlide) return activeSlide.getAttribute("data-media-id");

      const firstSlide = bundle.querySelector("[data-onura-media-slide]");
      if (firstSlide) return firstSlide.getAttribute("data-media-id");

      return null;
    };

    const getVisibleThumbs = () => {
      return Array.from(mediaThumbs).filter((thumb) => {
        return (
          !thumb.hasAttribute("hidden") &&
          !thumb.classList.contains("is-hidden-by-variant") &&
          thumb.offsetParent !== null
        );
      });
    };

    const getThumbStep = () => {
      const visibleThumbs = getVisibleThumbs();

      if (!visibleThumbs.length) return 96;

      const firstThumb = visibleThumbs[0];
      const styles = mediaThumbsWrapper ? window.getComputedStyle(mediaThumbsWrapper) : null;
      const gap = styles ? parseFloat(styles.rowGap || styles.gap || "10") || 10 : 10;

      return firstThumb.offsetHeight + gap;
    };

    const autoMoveThumbCarousel = (thumb) => {
      if (!mediaThumbsWrapper || !thumb) return;

      const visibleThumbs = getVisibleThumbs();

      if (visibleThumbs.length <= 6) return;

      const wrapperRect = mediaThumbsWrapper.getBoundingClientRect();
      const thumbRect = thumb.getBoundingClientRect();
      const step = getThumbStep();

      const isNearBottom = thumbRect.bottom > wrapperRect.bottom - step * 1.25;
      const isNearTop = thumbRect.top < wrapperRect.top + step * 1.25;

      if (isNearBottom) {
        mediaThumbsWrapper.scrollBy({
          top: step * 3,
          behavior: "smooth"
        });

        return;
      }

      if (isNearTop) {
        mediaThumbsWrapper.scrollBy({
          top: -step * 3,
          behavior: "smooth"
        });
      }
    };

    const activateThumb = (thumb) => {
      if (!thumb) return;

      const mediaId = thumb.getAttribute("data-media-id");

      if (!mediaId) return;

      setActiveMedia(mediaId);
      autoMoveThumbCarousel(thumb);
    };

    if (mediaThumbsWrapper) {
      let isDraggingThumbs = false;
      let didDragThumbs = false;
      let suppressNextClick = false;
      let startY = 0;
      let startScrollTop = 0;
      let startThumb = null;

      const isMobileGallery = () => {
        return window.matchMedia("(max-width: 989px)").matches;
      };

      mediaThumbsWrapper.addEventListener("click", (event) => {
        const thumb = event.target.closest("[data-onura-media-thumb]");

        if (!thumb || !mediaThumbsWrapper.contains(thumb)) return;

        event.preventDefault();
        event.stopPropagation();

        if (suppressNextClick) return;

        activateThumb(thumb);
      });

      mediaThumbsWrapper.addEventListener("pointerdown", (event) => {
        if (isMobileGallery()) return;
        if (event.button !== 0) return;

        startThumb = event.target.closest("[data-onura-media-thumb]");
        isDraggingThumbs = true;
        didDragThumbs = false;
        suppressNextClick = false;
        startY = event.clientY;
        startScrollTop = mediaThumbsWrapper.scrollTop;

        mediaThumbsWrapper.classList.add("is-onura-thumb-dragging");

        try {
          mediaThumbsWrapper.setPointerCapture(event.pointerId);
        } catch (error) {
          /* Pointer capture may fail on some browsers. */
        }
      });

      mediaThumbsWrapper.addEventListener("pointermove", (event) => {
        if (!isDraggingThumbs) return;

        const diff = event.clientY - startY;

        if (Math.abs(diff) > 5) {
          didDragThumbs = true;
          mediaThumbsWrapper.scrollTop = startScrollTop - diff;
        }
      });

      mediaThumbsWrapper.addEventListener("pointerup", (event) => {
        if (!isDraggingThumbs) return;

        isDraggingThumbs = false;
        mediaThumbsWrapper.classList.remove("is-onura-thumb-dragging");

        try {
          mediaThumbsWrapper.releasePointerCapture(event.pointerId);
        } catch (error) {
          /* Pointer may already be released. */
        }

        if (!didDragThumbs && startThumb) {
          suppressNextClick = true;
          activateThumb(startThumb);

          window.setTimeout(() => {
            suppressNextClick = false;
          }, 160);
        }

        if (didDragThumbs) {
          suppressNextClick = true;

          window.setTimeout(() => {
            suppressNextClick = false;
          }, 160);
        }

        startThumb = null;

        window.setTimeout(() => {
          didDragThumbs = false;
        }, 90);
      });

      mediaThumbsWrapper.addEventListener("pointercancel", () => {
        isDraggingThumbs = false;
        didDragThumbs = false;
        suppressNextClick = false;
        startThumb = null;
        mediaThumbsWrapper.classList.remove("is-onura-thumb-dragging");
      });
    } else {
      mediaThumbs.forEach((thumb) => {
        thumb.addEventListener("click", (event) => {
          event.preventDefault();
          activateThumb(thumb);
        });
      });
    }

    revealAllMedia();

    const initialMediaId = getInitialMediaId();

    if (initialMediaId) {
      setActiveMedia(initialMediaId);
    }
  };

  bundles.forEach((bundle) => {
    initProductGallery(bundle);

    const componentElements = Array.from(bundle.querySelectorAll("[data-onura-bundle-component]"));
    const addButton = bundle.querySelector("[data-onura-bundle-add-button]");
    const addButtonText = bundle.querySelector("[data-onura-bundle-add-button-text]");
    const messageNode = bundle.querySelector("[data-onura-bundle-message]");

    if (!addButton || !addButtonText) return;

    const labels = {
      add: bundle.getAttribute("data-label-add") || "Add bundle to cart",
      adding: bundle.getAttribute("data-label-adding") || "Adding bundle...",
      added: bundle.getAttribute("data-label-added") || "Bundle added",
      incomplete: bundle.getAttribute("data-label-incomplete") || "Complete your selections",
      retry: bundle.getAttribute("data-label-retry") || "Try again",
      available: bundle.getAttribute("data-label-available") || "Available",
      soldOut: bundle.getAttribute("data-label-sold-out") || "Sold out",
      combinationUnavailable:
        bundle.getAttribute("data-label-combination-unavailable") || "Combination unavailable"
    };

    let isAdding = false;
    let isConfirming = false;

    const componentStates = componentElements.map((component, index) => {
      const variantsScript = component.querySelector("[data-onura-bundle-component-variants]");
      const variantInput = component.querySelector("[data-onura-bundle-variant-input]");
      const optionGroups = Array.from(component.querySelectorAll("[data-onura-bundle-option-group]"));
      const optionInputs = Array.from(component.querySelectorAll("[data-onura-bundle-option-input]"));
      const mediaItems = Array.from(component.querySelectorAll("[data-onura-bundle-component-media]"));
      const statusDot = component.querySelector("[data-onura-bundle-status-dot]");
      const statusText = component.querySelector("[data-onura-bundle-status-text]");
      let variants = [];

      try {
        variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];
      } catch (error) {
        variants = [];
      }

      return {
        component,
        componentName: component.getAttribute("data-component-name") || `Component ${index + 1}`,
        variants,
        variantInput,
        optionGroups,
        optionInputs,
        mediaItems,
        statusDot,
        statusText,
        selectedVariant: null
      };
    });

    const setMessage = (message = "", type = "") => {
      if (!messageNode) return;

      messageNode.textContent = message;
      messageNode.hidden = !message;
      messageNode.classList.toggle("is-error", type === "error");
      messageNode.classList.toggle("is-success", type === "success");
    };

    const updateBundleButtonState = () => {
      const isComplete =
        componentStates.length > 0 &&
        componentStates.every((state) => state.selectedVariant && state.selectedVariant.available);

      addButton.disabled = isAdding || isConfirming || !isComplete;

      if (!isAdding && !isConfirming) {
        addButtonText.textContent = isComplete ? labels.add : labels.incomplete;
      }
    };

    const getSelectedOptions = (state) => {
      return state.optionGroups.map((group) => {
        return group.querySelector("[data-onura-bundle-option-input]:checked")?.value || null;
      });
    };

    const getVariantFromInput = (state) => {
      if (!state.variantInput?.value) return null;

      return (
        state.variants.find((variant) => Number(variant.id) === Number(state.variantInput.value)) || null
      );
    };

    const findSelectedVariant = (state) => {
      if (!state.variants.length) return null;

      const selectedOptions = getSelectedOptions(state);

      if (!selectedOptions.length) {
        return getVariantFromInput(state) || state.variants.find((variant) => variant.available) || state.variants[0] || null;
      }

      if (selectedOptions.some((option) => option === null)) return null;

      return (
        state.variants.find((variant) => {
          return (
            variant.options.length === selectedOptions.length &&
            variant.options.every((option, index) => option === selectedOptions[index])
          );
        }) || null
      );
    };

    const setActiveComponentMedia = (state, mediaId) => {
      if (!mediaId || !state.mediaItems.length) return;

      const matchingMedia = state.mediaItems.find((media) => {
        return media.getAttribute("data-media-id") === String(mediaId);
      });

      if (!matchingMedia) return;

      state.mediaItems.forEach((media) => {
        media.hidden = media !== matchingMedia;
      });
    };

    const updateComponentState = (state, options = {}) => {
      const variant = findSelectedVariant(state);
      state.selectedVariant = variant;

      if (state.variantInput) {
        state.variantInput.value = variant?.id || "";
      }

      const isAvailable = Boolean(variant?.available);
      state.component.classList.toggle("is-unavailable", !isAvailable);

      if (state.statusDot) {
        state.statusDot.classList.toggle("is-available", isAvailable);
        state.statusDot.classList.toggle("is-unavailable", !isAvailable);
      }

      if (state.statusText) {
        if (!variant) {
          state.statusText.textContent = labels.combinationUnavailable;
        } else {
          state.statusText.textContent = isAvailable ? labels.available : labels.soldOut;
        }
      }

      if (options.updateMedia && variant?.featured_media?.id) {
        setActiveComponentMedia(state, variant.featured_media.id);
      }

      setMessage();
      updateBundleButtonState();
    };

    componentStates.forEach((state) => {
      state.optionInputs.forEach((input) => {
        input.addEventListener("change", () => {
          updateComponentState(state, { updateMedia: true });
        });
      });

      state.selectedVariant = getVariantFromInput(state) || findSelectedVariant(state);
      updateComponentState(state, { updateMedia: false });
    });

    updateBundleButtonState();

    addButton.addEventListener("click", async () => {
      componentStates.forEach((state) => updateComponentState(state, { updateMedia: false }));

      const isComplete =
        componentStates.length > 0 &&
        componentStates.every((state) => state.selectedVariant && state.selectedVariant.available);

      if (!isComplete || isAdding || isConfirming) {
        updateBundleButtonState();
        return;
      }

      const bundleId = createBundleId();
      const bundleName = bundle.getAttribute("data-bundle-name") || "ONURA bundle";
      const quantity = getBundleQuantity(bundle);
      const items = componentStates.map((state) => ({
        id: Number(state.selectedVariant.id),
        quantity,
        properties: {
          _onura_bundle_id: bundleId,
          _onura_bundle_name: bundleName,
          _onura_bundle_component: state.componentName
        }
      }));

      isAdding = true;
      bundle.classList.add("is-adding");
      addButton.disabled = true;
      addButtonText.textContent = labels.adding;
      setMessage();

      const cartDrawer = document.querySelector("cart-drawer");
      cartDrawer?.setActiveElement?.(addButton);

      try {
        const cartResponse = await addItemsToCart(items);

        if (cartDrawer && typeof cartDrawer.renderContents === "function") {
          cartDrawer.classList.remove("is-empty");
          cartDrawer.renderContents(cartResponse);
        } else {
          document.dispatchEvent(new CustomEvent("onura:cart:update"));
          document.dispatchEvent(new CustomEvent("cart:refresh"));
          document.dispatchEvent(new CustomEvent("cart:updated"));
        }

        isAdding = false;
        isConfirming = true;
        bundle.classList.remove("is-adding");
        bundle.classList.add("is-added");
        addButton.disabled = true;
        addButtonText.textContent = labels.added;
        setMessage(labels.added, "success");

        window.setTimeout(() => {
          isConfirming = false;
          bundle.classList.remove("is-added");
          setMessage();
          updateBundleButtonState();
        }, 1400);
      } catch (error) {
        isAdding = false;
        bundle.classList.remove("is-adding");
        setMessage(error.message, "error");
        updateBundleButtonState();
        addButtonText.textContent = labels.retry;
        console.error(error);
      }
    });
  });
})();
